import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '@hospitality-bot/shared/utils';
import {
  BookingSearchPage,
  BookingSearchResult,
} from '../types/booking-search.type';

@Injectable({ providedIn: 'root' })
export class BookingSearchService extends ApiService {
  /**
   * GET /api/v1/search/bookings?keyId={reservationId}&offset=&limit=
   * GET /api/v1/search/bookings?key={query}&offset=&limit=
   */
  searchBookings(
    entityId: string,
    searchQuery: string,
    options?: { useKeyId?: boolean; offset?: number; limit?: number }
  ): Observable<BookingSearchPage> {
    const value = encodeURIComponent((searchQuery || '').trim());
    const empty: BookingSearchPage = {
      results: [],
      total: 0,
      recordCount: 0,
    };
    if (!value || !entityId) {
      return of(empty);
    }

    const param = options?.useKeyId ? 'id' : 'key';
    const offset = options?.offset ?? 0;
    const limit = options?.limit ?? 10;
    const config = { headers: { 'entity-id': entityId } };

    return this.get(
      `/api/v1/search/bookings?${param}=${value}&offset=${offset}&limit=${limit}`,
      config
    ).pipe(
      map((res) => this.mapPage(res)),
      catchError(() => of(empty))
    );
  }

  private mapPage(res: any): BookingSearchPage {
    if (!res) {
      return { results: [], total: 0, recordCount: 0 };
    }

    const list = this.extractList(res);
    return {
      results: this.mapResults(list),
      total: res.total ?? res.totalCount ?? 0,
      recordCount: list.length,
    };
  }

  private extractList(res: any): any[] {
    if (Array.isArray(res)) return res;
    return res.reservations || res.records || res.data || res.bookings || [];
  }

  private mapResults(list: any[]): BookingSearchResult[] {
    if (!Array.isArray(list) || !list.length) return [];

    const rows: BookingSearchResult[] = [];
    list.forEach((item: any) => {
      if (!item) return;
      rows.push(this.mapReservationRow(item, 'Primary'));
      const sharers =
        item.sharerGuests ||
        item.guestDetails?.sharerGuests ||
        item.sharers ||
        item.sharingGuests ||
        [];
      (Array.isArray(sharers) ? sharers : []).forEach((sharer: any) => {
        rows.push(this.mapSharerRow(item, sharer));
      });
    });

    const seen = new Set<string>();
    return rows.filter((row) => {
      const key = `${row.id || row.res}|${row.guest}|${row.role || ''}`;
      if (seen.has(key) || !row.guest) return false;
      seen.add(key);
      return true;
    });
  }

  private mapReservationRow(item: any, role?: string): BookingSearchResult {
    const guest =
      item.primaryGuestName ||
      item.guestName ||
      this.formatGuestName(item.guest || item.guestDetails?.primaryGuest) ||
      item.label ||
      'Guest';

    return {
      id: item.id || item.reservationId,
      res:
        item.number ||
        item.bookingNumber ||
        item.bookingNo ||
        item.reservationNumber ||
        item.id ||
        '',
      guest,
      room: this.resolveRoom(item),
      phone: this.resolvePhone(
        item.guest || item.guestDetails?.primaryGuest,
        item
      ),
      email: this.resolveEmail(
        item.guest || item.guestDetails?.primaryGuest,
        item
      ),
      role,
      groupCode: item.groupCode || '',
      raw: item,
    };
  }

  private mapSharerRow(item: any, sharer: any): BookingSearchResult {
    const guest =
      this.formatGuestName(sharer) ||
      sharer.name ||
      sharer.guestName ||
      'Sharer';
    const reservationId =
      sharer.reservationId ||
      sharer.bookingId ||
      item.id ||
      item.reservationId;

    return {
      id: reservationId,
      res:
        sharer.number ||
        sharer.bookingNumber ||
        item.number ||
        item.bookingNumber ||
        reservationId ||
        '',
      guest,
      room: this.resolveRoom(sharer) || this.resolveRoom(item),
      phone: this.resolvePhone(sharer, sharer),
      email: this.resolveEmail(sharer, sharer),
      role: 'Sharer',
      groupCode: item.groupCode || '',
      raw: sharer,
    };
  }

  private formatGuestName(guest: any): string {
    if (!guest) return '';
    if (typeof guest === 'string') return guest.trim();
    return `${guest.firstName || guest.first_name || ''} ${
      guest.lastName || guest.last_name || ''
    }`.trim();
  }

  private resolveRoom(item: any): string {
    return (
      item?.roomNo ||
      item?.roomNumber ||
      item?.tableNumberOrRoomNumber ||
      item?.rooms?.[0]?.roomNumber ||
      item?.bookingItems?.[0]?.roomDetails?.roomNumber ||
      '—'
    );
  }

  private resolvePhone(guest: any, fallback?: any): string {
    return (
      guest?.contactDetails?.contactNumber ||
      guest?.phoneNumber ||
      guest?.phone ||
      fallback?.phoneNumber ||
      fallback?.phone ||
      '—'
    );
  }

  private resolveEmail(guest: any, fallback?: any): string {
    return (
      guest?.contactDetails?.emailId ||
      guest?.contactDetails?.email ||
      guest?.email ||
      fallback?.email ||
      ''
    );
  }
}
